import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CurrencyDollar, Truck, MapTrifold, ShieldCheck, Wrench, Buildings,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Department Queues — cross-team operational visibility for managers and
 * administrators. Each tile is a count of non-terminal (Open / In Progress /
 * Waiting) tasks in the single platform Task Engine, filtered by task_type.
 *
 * Clicking a tile drills into the Tasks page pre-filtered to that queue. This
 * is a summary widget only; there is no separate task system per department.
 */
const QUEUES = [
  { key: "Accounts Receivable", label: "Accounts Receivable", icon: CurrencyDollar, tone: "emerald" },
  { key: "Dispatch",            label: "Dispatch",            icon: Truck,          tone: "blue"    },
  { key: "Fleet",               label: "Fleet",               icon: MapTrifold,     tone: "indigo"  },
  { key: "Safety",              label: "Safety",              icon: ShieldCheck,    tone: "amber"   },
  { key: "Service",             label: "Service",             icon: Wrench,         tone: "slate"   },
];

const TONE = {
  emerald: "border-status-success-bg bg-status-success-bg text-status-success-fg hover:brightness-95",
  blue:    "border-status-info-bg bg-status-info-bg text-status-info-fg hover:brightness-95",
  indigo:  "border-dept-fleet bg-dept-fleet/10 text-dept-fleet hover:brightness-95",
  amber:   "border-status-warning-bg bg-status-warning-bg text-status-warning-fg hover:brightness-95",
  slate:   "border-dept-service bg-dept-service/10 text-dept-service hover:brightness-95",
};

export default function DepartmentQueuesWidget({ refreshKey = 0 }) {
  const nav = useNavigate();
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tasks/queue-counts`);
      setCounts(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  // Parent-triggered refresh — bumps whenever a new task is created elsewhere
  // in the Dashboard so the department queue counts stay in sync.
  useEffect(() => {
    if (refreshKey > 0) load();
  }, [refreshKey]);

  // The endpoint itself gates on role, so if the caller isn't a manager we
  // simply render nothing — the tile grid is manager-only oversight.
  if (forbidden) return null;

  return (
    <div
      data-testid="department-queues-widget"
      className="rounded-md border border-divider bg-surface"
    >
      <div className="border-b border-divider px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Buildings size={16} weight="regular" className="text-ink-secondary" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">
            Department Queues
          </h3>
        </div>
        <p className="mt-0.5 text-[11px] text-ink-muted">
          Non-terminal tasks · single Task Engine, filtered by type
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 xl:grid-cols-5">
        {QUEUES.map((q) => {
          const Icon = q.icon;
          const value = counts[q.key] ?? 0;
          return (
            <button
              key={q.key}
              data-testid={`dept-queue-${q.key.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => nav(`/tasks?tab=team&task_type=${encodeURIComponent(q.key)}`)}
              className={`flex flex-col items-start gap-1.5 rounded-md border p-3 text-left transition ${TONE[q.tone]}`}
            >
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider">
                <Icon size={13} weight="bold" /> {q.label}
              </div>
              <div className="text-3xl font-bold leading-none">
                {loading ? "—" : value}
              </div>
              <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">
                Open in queue
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
